import { useEffect } from 'react';
import { Outlet, ScrollRestoration, useLocation } from 'react-router-dom';

import { Footer } from './components/Footer';
import { TopBar } from './components/TopBar';

/**
 * Globally enhance all `.eyebrow` elements with a one-shot scanline reveal
 * the first time they enter the viewport (A10).
 *
 * SSG-safe: the HTML still ships with full eyebrow text and no JS-only class,
 * so crawlers and reduced-motion users see static eyebrows. JS only adds the
 * decorative `.anim-eyebrow` class on intersect.
 */
const useEyebrowReveal = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof IntersectionObserver === 'undefined') return;

    const elements = Array.from(document.querySelectorAll<HTMLElement>('.eyebrow'));
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('anim-eyebrow');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.5, rootMargin: '0px 0px -5% 0px' },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [location.pathname]);
};

export const App = () => {
  useEyebrowReveal();

  return (
    <>
      <a href="#main" className="sr-only">
        Skip to content
      </a>
      <TopBar />
      <main id="main">
        <Outlet />
      </main>
      <Footer />
      <ScrollRestoration />
    </>
  );
};
